import {lookup} from './lookup';
import languageObject from './locales/en.json';

type LanguageObject = typeof languageObject; // {[key: string]: string | LanguageObject };

type Plural = 'zero' | 'one' | 'other';

const dot = '.' as const;
type Dot = typeof dot;

/**
 * Derives paths to all string properties as union of arrays of strings, e.g.
 * {a:{b:'x'}, c: 'y'} => ['a', 'b'] | ['c']
 */
type ArrayPathsToStringProps<T> = T extends string ? [] : {
  [K in Extract<keyof T, string>]: [K, ...ArrayPathsToStringProps<T[K]>]
}[Extract<keyof T, string>];

/**
 * Joins union of path arrays to union of path strings with dot-separation, e.g. 
 * ['a', 'b'] | ['c'] => 'a.b' | 'c'
 * Any plural parts are filtered out 
 */
type Join<T extends string[], TDot extends string, TPlural> =
  T extends [] ? never :
  // Single part => part
  T extends [infer F] ? F :
  // Part with plural => part
  T extends [infer F, TPlural] ? F :
  // Part with more parts => 'part.<more parts>' 
  T extends [infer F, ...infer R] ?
    F extends string ? R extends string[] ? `${F}${TDot}${Join<R, TDot, TPlural>}` : never : never : string;

/**
 * Looks up string literal in a nested key/string map
 */
type LookupLiteral<TO extends {[key: string]: any}, TPath extends string, D extends string, TPlural extends string> = 
  // Navigate nested object
  TPath extends `${infer K}${D}${infer R}` ? K extends string ?
    TO[K] extends {} ? LookupLiteral<TO[K], R, D, TPlural> : never : never :
  // Lookup string literal
  TO[TPath] extends string ? TO[TPath] : 
  // TODO: Proper lookup of plural literals
  '{{count}}';

/**
 * Parse embedded values of string literal to {key: string|number} map, e.g.
 * 'Welcome {{firstname}} {{lastname}}' => { firstname: string | number, lastname: string | number }
 */  
type Parse<T extends string, TAggregate extends ({} | undefined) = undefined> = 
  // Any match '...{{key}}rest'
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  T extends `${infer _}{{${infer K}}}${infer R}` ? 
    K extends string ? 
      Parse<R, (TAggregate extends {} ? TAggregate : {}) & { [key in K]: string | number }> : never : 
  // Otherwise, aggregate
  TAggregate;

/**
 * Builds { path: argType } map for all path strings
 */
type PathArgsMap<TO extends LanguageObject, TPath extends string, TDot extends string, TPlural extends string> = 
{[key in TPath]: Parse<LookupLiteral<TO, key, TDot, TPlural>>};

/**
 * Derives union of all keys with out arguments
 */
type NoArgKeys<T extends {}> = {
  [key in keyof T]: T[key] extends {} ? never : key;
}[keyof T]

type ArrayPaths = ArrayPathsToStringProps<LanguageObject>;

type StringPaths = Join<ArrayPaths, Dot, Plural>

type KeyArgs = PathArgsMap<LanguageObject, StringPaths, Dot, Plural>;

type KeysWithoutArgs = NoArgKeys<KeyArgs>;

type KeysWithArgs = Omit<KeyArgs, KeysWithoutArgs>;

type Args = { [key: string]: number | string};

interface Translate {
  <TKey extends keyof KeysWithArgs>(key: TKey, args: KeysWithArgs[TKey] & Args): string;
  (key: KeysWithoutArgs, args?: Args): string;
}

export const t: Translate =  (key: string, args?: any): string => {
  return lookup(key, args, dot, languageObject);
};
